import { Box, Paper, Typography, useTheme } from '@mui/material';
import Prism from 'prismjs';
import React, { useEffect, useState } from 'react';
// Import only the basic languages that are available by default
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-markup'; // HTML/XML support
import 'prismjs/components/prism-typescript';
import './prism-kor.css'; // We'll create this custom theme file next

export interface FileRendererProps {
  name: string;
  content: string | ArrayBufferLike;
  type: 'bin' | 'text';
  encoding?: string;
  url?: string;
}

// Check if Prism is loaded correctly
const isPrismLoaded = () => {
  return typeof Prism !== 'undefined' && typeof Prism.highlight === 'function';
};

// Check if a language is supported by Prism
const isLanguageSupported = (language: string): boolean => {
  if (!isPrismLoaded()) return false;
  return !!Prism.languages[language];
};

const getLanguageFromFileName = (fileName: string): string => {
  if (!fileName) return 'none';
  const extension = fileName.split('.').pop()?.toLowerCase() || '';

  // Map file extensions to Prism language names
  const extensionMap: Record<string, string> = {
    js: 'javascript',
    jsx: 'jsx',
    ts: 'typescript',
    tsx: 'typescript', // Fallback to typescript
    py: 'javascript', // Fallback to javascript
    java: 'javascript', // Fallback to javascript
    go: 'javascript', // Fallback to javascript
    rs: 'javascript', // Fallback to javascript
    json: 'json',
    yaml: 'javascript', // Fallback to javascript
    yml: 'javascript', // Fallback to javascript
    md: 'markdown',
    sh: 'bash',
    bash: 'bash',
    sql: 'javascript', // Fallback to javascript
    csv: 'none',
    css: 'css',
    scss: 'css', // Fallback to css
    html: 'markup',
    xml: 'markup',
    txt: 'none',
  };

  const language = extensionMap[extension] || 'none';

  // If language is not supported, fallback to plaintext
  if (language !== 'none' && !isLanguageSupported(language)) {
    return 'none';
  }

  return language;
};

const renderBinaryContent = (
  content: ArrayBufferLike,
  name: string,
  url?: string
) => {
  const size = content.byteLength;
  const formattedSize =
    size < 1024
      ? `${size} bytes`
      : size < 1024 * 1024
        ? `${(size / 1024).toFixed(2)} KB`
        : `${(size / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Binary File: {name}</Typography>
      <Typography variant="body1">Size: {formattedSize}</Typography>
      {url && (
        <Typography
          variant="body2"
          component="a"
          href={url}
          download={name}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            display: 'block',
            mt: 1,
            cursor: 'pointer',
          }}
        >
          Download File
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Binary content cannot be displayed directly.
      </Typography>
    </Box>
  );
};

const escapeHTML = (content: string) => {
  if (!content) return '';
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const renderTextContent = (content: string, language: string) => {
  // For syntax highlighting
  let highlightedContent;

  try {
    // If Prism is loaded and language is supported, use Prism highlighting
    if (isPrismLoaded() && language !== 'none' && Prism.languages[language]) {
      if (language === 'json') {
        content = JSON.stringify(JSON.parse(content), null, 2);
      }
      highlightedContent = Prism.highlight(
        content,
        Prism.languages[language],
        language
      );
    } else {
      // Otherwise use plaintext but escape HTML
      highlightedContent = escapeHTML(content);
    }
  } catch (error) {
    console.error('Error highlighting code:', error);
    // Fallback to plain escaped text
    highlightedContent = escapeHTML(content);
  }

  return (
    <Box sx={{ p: 0, overflowY: 'auto', overflowX: 'hidden' }}>
      <pre className={language !== 'none' ? `language-${language}` : ''}>
        <code
          style={{
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'break-all',
          }}
          className={language !== 'none' ? `language-${language}` : ''}
          dangerouslySetInnerHTML={{ __html: highlightedContent }}
        />
      </pre>
    </Box>
  );
};

const FileRenderer: React.FC<FileRendererProps> = ({
  name,
  content,
  type,
  encoding = 'utf-8',
  url,
}) => {
  const theme = useTheme();
  const [processedContent, setProcessedContent] = useState<string | null>(null);
  const language = getLanguageFromFileName(name);

  useEffect(() => {
    // Highlight code on component mount and when content changes
    if (isPrismLoaded()) {
      try {
        Prism.highlightAll();
      } catch (error) {
        console.error('Error in Prism.highlightAll():', error);
      }
    }
  }, [content]);

  useEffect(() => {
    // Process content based on type
    try {
      if (type === 'text') {
        setProcessedContent(content as string);
      } else if (type === 'bin' && content instanceof ArrayBuffer) {
        // For binary files, we don't need to process them here
        // as we'll display binary information differently
        setProcessedContent(null);
      } else if (typeof content === 'string') {
        setProcessedContent(content);
      }
    } catch (error) {
      console.error('Error processing content:', error);
      // Fallback to displaying as plain text if possible
      if (typeof content === 'string') {
        setProcessedContent(content);
      } else {
        setProcessedContent('Error processing content');
      }
    }
  }, [content, type, encoding]);

  return (
    <Paper
      elevation={2}
      sx={{
        height: '100%',
        overflow: 'auto',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.95)'
            : 'rgba(250, 250, 250, 0.95)',
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(50, 50, 50, 0.8)'
              : 'rgba(230, 230, 230, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
          {name}
        </Typography>
        {url && (
          <Typography
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: '0.8rem',
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              '&:hover': {
                color: theme.palette.primary.dark,
              },
            }}
          >
            Source
          </Typography>
        )}
      </Box>

      <Box sx={{ maxHeight: 'calc(100% - 40px)', overflow: 'auto' }}>
        {type === 'bin' && content instanceof ArrayBuffer ? (
          renderBinaryContent(content, name, url)
        ) : processedContent !== null ? (
          renderTextContent(processedContent, language)
        ) : (
          <Typography variant="body1" sx={{ p: 2 }}>
            Unable to render content
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default FileRenderer;
